import React, { useEffect, useState } from 'react';
import { apiService } from '../../../services/apiService';
import { AdminLink } from '../adminNav';

/**
 * Billing → Emails: every e-mail a payment event sends, in one place
 * (Backend billing_service.admin_billing_emails):
 *   - ours to the customer: the billing templates, previewed with sample
 *     values, each linking to its editor in Email Center → Templates;
 *   - ours to the team inbox: the subscription started / FAILED / refund
 *     alerts, rendered with a sample account and a sample failure cause;
 *   - Razorpay's own customer notifications, which Razorpay sends itself.
 */

const Section: React.FC<{ title: string; note?: string; children: React.ReactNode }> = ({ title, note, children }) => (
  <div className="rounded-xl border border-grayscale-300 bg-white p-4 flex flex-col gap-3">
    <div>
      <div className="text-sm font-bold text-grayscale-1300 font-grotesk">{title}</div>
      {note && <div className="text-xs text-grayscale-1100 mt-1 max-w-3xl">{note}</div>}
    </div>
    {children}
  </div>
);

const BillingEmailsPanel: React.FC = () => {
  const [data, setData] = useState<any | null>(null);
  const [err, setErr] = useState('');
  const [open, setOpen] = useState<string>('');

  useEffect(() => {
    let live = true;
    const uid = apiService.getStoredUserId() || '';
    apiService.adminBillingEmails(uid).then(r => {
      if (!live) return;
      if (r.success && r.data?.success) setData(r.data);
      else setErr(r.error || 'Could not load the billing e-mails');
    });
    return () => { live = false; };
  }, []);

  if (err) return <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2">{err}</div>;
  if (!data) return <div className="text-sm text-grayscale-1100">Loading…</div>;

  const toggle = (k: string) => setOpen(o => (o === k ? '' : k));

  return (
    <div className="flex flex-col gap-4">
      <Section title={`Sent to the customer (${(data.customer || []).length})`}
        note="Our templates. Previews use each template's sample values; edit the wording in Email Center → Templates.">
        <div className="flex flex-col divide-y divide-grayscale-200">
          {(data.customer || []).map((t: any) => (
            <div key={t.key} className="py-2.5 flex flex-col gap-1.5">
              <div className="flex items-start justify-between gap-3 flex-wrap">
                <div className="min-w-0">
                  <div className="text-sm font-medium text-grayscale-1300">
                    {t.name}
                    {t.edited && <span className="ml-2 text-[11px] px-1.5 py-0.5 rounded bg-amber-50 text-amber-700 border border-amber-200">edited{t.updated_by ? ` by ${t.updated_by}` : ''}</span>}
                  </div>
                  <div className="text-xs text-grayscale-1100">When: {t.trigger || '—'}</div>
                  <div className="text-xs text-grayscale-1200">Subject: {t.subject}</div>
                </div>
                <div className="flex items-center gap-3 shrink-0 text-xs">
                  <button type="button" onClick={() => toggle(`c:${t.key}`)} className="text-primary-700 underline underline-offset-2">
                    {open === `c:${t.key}` ? 'Hide preview' : 'Preview'}
                  </button>
                  <AdminLink to={{ group: 'email', tab: 'templates', template: t.key }}>Edit in Email Center</AdminLink>
                </div>
              </div>
              {open === `c:${t.key}` && (
                <pre className="whitespace-pre-wrap text-xs text-grayscale-1300 bg-grayscale-100 border border-grayscale-200 rounded-lg p-3 font-sans">{t.preview}</pre>
              )}
            </div>
          ))}
        </div>
      </Section>

      <Section title="Sent to us" note={`Alerts to ${(data.internal || [])[0]?.to || 'the team inbox'} for every payment event, naming the account. A failure starts with "What happened" in plain words.`}>
        <div className="flex flex-col divide-y divide-grayscale-200">
          {(data.internal || []).map((m: any, i: number) => (
            <div key={i} className="py-2.5 flex flex-col gap-1.5">
              <div className="flex items-start justify-between gap-3 flex-wrap">
                <div className="min-w-0">
                  <div className="text-sm font-medium text-grayscale-1300">{m.name}</div>
                  <div className="text-xs text-grayscale-1100">When: {m.when}</div>
                  <div className="text-xs text-grayscale-1200">Subject: {m.subject}</div>
                </div>
                <button type="button" onClick={() => toggle(`i:${i}`)} className="text-xs text-primary-700 underline underline-offset-2 shrink-0">
                  {open === `i:${i}` ? 'Hide preview' : 'Preview'}
                </button>
              </div>
              {open === `i:${i}` && (
                <iframe title={m.name} sandbox="" srcDoc={m.html}
                  className="w-full h-[420px] border border-grayscale-200 rounded-lg bg-white" />
              )}
            </div>
          ))}
        </div>
      </Section>

      <Section title="Sent by Razorpay" note={data.razorpay_note}>
        <ul className="flex flex-col gap-1.5">
          {(data.razorpay || []).map((r: any, i: number) => (
            <li key={i} className="text-sm text-grayscale-1300">
              <span className="font-medium">{r.name}</span>
              <span className="text-grayscale-1100"> — {r.when}</span>
            </li>
          ))}
        </ul>
      </Section>
    </div>
  );
};

export default BillingEmailsPanel;
